import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { BrandLogo } from "./BrandMark";

const NAV = [
  { label: "Home", href: "/" },
  { label: "Our Story", href: "/our-story" },
  { label: "Products", href: "/products" },
  { label: "Reviews", href: "/reviews" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totalUnits } = useCart();
  const { user } = useCustomerAuth();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={
        "sticky top-0 z-40 transition-[background,box-shadow,backdrop-filter] duration-300 " +
        (scrolled
          ? "bg-[rgba(250,247,242,0.9)] backdrop-blur-md shadow-[0_1px_0_rgba(201,169,97,0.25)]"
          : "bg-[rgba(250,247,242,0.7)] backdrop-blur-sm")
      }
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="HerbKranti — home">
          <BrandLogo variant="light" markSize={40} wordSize={20} showTagline={true} taglineSize={9} hideTaglineOnMobile={true} testId="header-brand-logo" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              end={n.href === "/"}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                "text-[13.5px] tracking-wide transition-colors duration-200 " +
                (isActive ? "text-[var(--hk-orange-deep)]" : "text-[var(--hk-ink-2)] hover:text-[var(--hk-orange-deep)]")
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to={user ? "/account" : "/login"}
            data-testid="header-account-btn"
            className="hidden sm:inline-flex items-center gap-2 h-10 px-3.5 rounded-full border border-[var(--hk-border)] bg-white hover:border-[var(--hk-orange)] transition-colors text-[13px] text-[var(--hk-ink-2)]"
            title={user ? `Signed in as ${user.name}` : "Sign in"}
          >
            <User size={15} />
            <span className="hidden md:inline">{user ? user.name.split(" ")[0] : "Sign in"}</span>
          </Link>
          <Link
            to="/cart"
            data-testid="header-cart-btn"
            className="relative inline-flex items-center gap-2 h-10 px-3.5 rounded-full border border-[var(--hk-border)] bg-white hover:border-[var(--hk-orange)] transition-colors text-[13px] text-[var(--hk-ink-2)]"
          >
            <ShoppingBag size={15} />
            <span className="hidden sm:inline">Cart</span>
            {totalUnits > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--hk-orange)] text-white text-[11px] font-medium flex items-center justify-center">
                {totalUnits}
              </span>
            )}
          </Link>
          <Link to="/products" className="hidden md:inline-flex hk-btn-primary" data-testid="header-shop-btn">
            Shop
          </Link>
          <button
            className="lg:hidden p-2 rounded-md text-[var(--hk-ink)]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--hk-border)] bg-[var(--hk-ivory)]/98 backdrop-blur-md">
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                to={n.href}
                end={n.href === "/"}
                className={({ isActive }) =>
                  "text-[15px] " + (isActive ? "text-[var(--hk-orange-deep)]" : "text-[var(--hk-ink-2)]")
                }
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/products" className="hk-btn-primary self-start mt-2" data-testid="mobile-shop-btn">
              Shop
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
